import * as React from 'react'
import { Command, defaultFilter } from 'cmdk'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Search } from 'lucide-react'
import { useAllScreens } from './screens'
import { useSession, useWorkspaceId, type Session } from './session'
import { screenHref } from './AppShell'

/**
 * Ctrl/⌘ K: jump to any screen you can open, any workspace, or run a search
 * provider an area registered (memories, traces…).
 */
export interface PaletteResult {
  id: string
  label: string
  hint?: string
  href: string
}
export interface PaletteContext {
  workspaceId: string | null
  session: Session
  queryClient: QueryClient
}
export type PaletteProvider = {
  name: string
  /** Group heading; defaults to `name`. */
  heading?: (ctx: PaletteContext) => string
  search: (q: string, ctx: PaletteContext) => Promise<PaletteResult[]>
}
const providers: PaletteProvider[] = []
/** Areas register search providers at import time (see paletteProviders.ts). */
export function registerPaletteProvider(p: PaletteProvider): void {
  providers.push(p)
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const session = useSession()
  const screens = useAllScreens()
  const ws = useWorkspaceId()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [q, setQ] = React.useState('')
  const [remote, setRemote] = React.useState<Record<string, { heading: string; results: PaletteResult[] }>>({})
  const [pending, setPending] = React.useState(0)
  const searching = q.trim().length >= 2
  const perms = new Set(session.permissions as string[])
  const visible = screens.filter(s => !s.hidden && (s.platform ? session.is_super_admin : !s.perm || (Array.isArray(s.perm) ? s.perm : [s.perm]).some(p => perms.has(p))))

  React.useEffect(() => {
    if (!open) {
      setQ('')
      setRemote({})
    }
  }, [open])
  React.useEffect(() => {
    if (!open || q.trim().length < 2) {
      setRemote({})
      setPending(0)
      return
    }
    // Results from an older query must never overwrite a newer one.
    let stale = false
    const t = setTimeout(() => {
      const ctx: PaletteContext = { workspaceId: ws, session, queryClient }
      setPending(providers.length)
      for (const p of providers) {
        const heading = p.heading?.(ctx) ?? p.name
        void p
          .search(q.trim(), ctx)
          .then(
            r => !stale && setRemote(prev => ({ ...prev, [p.name]: { heading, results: r.slice(0, 6) } })),
            () => !stale && setRemote(prev => ({ ...prev, [p.name]: { heading, results: [] } })),
          )
          .finally(() => !stale && setPending(n => Math.max(0, n - 1)))
      }
    }, 220)
    return () => {
      stale = true
      clearTimeout(t)
    }
  }, [open, q, ws, session, queryClient])

  const go = (href: string) => {
    onOpenChange(false)
    void navigate({ to: href })
  }
  const item = 'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[12.5px] text-ink aria-selected:bg-soft [&_svg]:size-4 [&_svg]:text-muted'

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[#0b1425]/45" />
        <DialogPrimitive.Content className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-32px)] max-w-[600px] -translate-x-1/2 overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl">
          <DialogPrimitive.Title className="sr-only">Search ESM</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Jump to a screen, workspace or record</DialogPrimitive.Description>
          {/* Server results are already matched (often by meaning), so the
              local fuzzy filter only ranks screens and the workspace list. */}
          <Command label="Search ESM" shouldFilter filter={(value, search, keywords) => (value.startsWith('remote:') ? 1 : defaultFilter(value, search, keywords))}>
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search className="size-4 text-muted" />
              <Command.Input value={q} onValueChange={setQ} placeholder={perms.has('memory.read') && ws ? 'Search screens, workspaces and memory…' : 'Search screens and workspaces…'} className="h-12 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-muted" />
            </div>
            <Command.List className="max-h-[420px] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-8 text-center text-[12px] text-muted">{searching && pending > 0 ? 'Searching…' : 'No matches.'}</Command.Empty>
              {Object.entries(remote).map(([name, { heading, results }]) =>
                results.length ? (
                  <Command.Group key={name} heading={heading} className="mb-1 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted">
                    {results.map(r => (
                      <Command.Item key={r.id} value={`remote:${name}:${r.id} ${r.label}`} onSelect={() => go(r.href)} className={item}>
                        <span className="truncate">{r.label}</span>
                        {r.hint && <span className="ml-auto truncate text-[11px] text-muted">{r.hint}</span>}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ) : null,
              )}
              <Command.Group heading="Screens" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted">
                {visible.map(s => {
                  const Icon = s.icon
                  return (
                    <Command.Item key={s.routeId} value={`${s.title} ${s.group} ${s.id} ${s.description ?? ''}`} onSelect={() => go(screenHref(s, ws))} className={item}>
                      <Icon />
                      <span>{s.title}</span>
                      <span className="ml-auto text-[11px] text-muted">{s.group === 'Investigate' ? 'Trace' : s.group}</span>
                    </Command.Item>
                  )
                })}
              </Command.Group>
              {/* With a search typed, the Workspaces provider answers instead. */}
              {!searching && session.workspaces.length > 1 && (
                <Command.Group heading="Workspaces" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted">
                  {session.workspaces.map(w => (
                    <Command.Item key={w.id} value={`workspace ${w.name}`} onSelect={() => go(`/w/${w.id}/overview`)} className={item}>
                      <span>{w.name}</span>
                      <span className="ml-auto text-[11px] text-muted">Workspace</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
